/**
 * Delete (not withdraw) one or more published builds — the Registry page's
 * hard-delete flow (src/lib/buildDeletion.ts has the rules and the tests;
 * plan internal/compiler/plans/2026-10-14-hard-delete-builds.md).
 *
 * 1. Dry run: lists everything that would be removed (registry objects and
 *    bytes, platform packages, metadata, pipeline rows, caches, tombstones).
 * 2. A refusal that needs a decision becomes explicit buttons ("Delete and
 *    re-point 7.2.0 to <previous build>", "Delete the whole package
 *    version"...). Nothing is chosen for the operator.
 * 3. A reason and the exact confirmation text are required; the lockfile
 *    consequence is shown right above the confirm field.
 * 4. A deletion a store failed on is retried automatically; "Retry now"
 *    nudges it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import {
  EMPTY_CHOICES,
  actionsFor,
  canConfirm,
  confirmPhrase,
  deleteBody,
  deletionStatusLine,
  describePlan,
  isFinal,
  lockfileWarning,
  planBody,
  type Choices,
  type DeletionState,
  type PlanRefusal,
  type PlanResponse,
} from "@/lib/buildDeletion";

interface DeletionResult {
  ok: boolean;
  id: string;
  state: DeletionState;
  complete: boolean;
  error: string | null;
}

export function DeleteBuildsDialog({
  pkg,
  versions,
  onClose,
  onDone,
}: {
  pkg: string;
  /** The selected build versions; the dialog is open while non-empty. */
  versions: string[];
  onClose: () => void;
  /** Called once the deletion finished (and when closing after any attempt),
   *  so the page reloads the builds. */
  onDone: () => void;
}) {
  const token = useAuthToken();
  const open = versions.length > 0;
  const [choices, setChoices] = useState<Choices>(EMPTY_CHOICES);
  const [resp, setResp] = useState<PlanResponse | null>(null);
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<DeletionResult | null>(null);
  const attempted = useRef(false);
  const key = versions.join("|");

  // A new selection starts over.
  useEffect(() => {
    setChoices(EMPTY_CHOICES);
    setResp(null);
    setError(null);
    setReason("");
    setTyped("");
    setResult(null);
    attempted.current = false;
  }, [key, pkg]);

  const plan = useCallback(async () => {
    setPlanning(true);
    setError(null);
    try {
      setResp(
        await api<PlanResponse>(token, "/publish/builds/plan", {
          method: "POST",
          body: JSON.stringify(planBody(pkg, versions, choices)),
        }),
      );
    } catch (e) {
      setResp(null);
      setError(
        e instanceof ApiError ? e.message : "could not plan the deletion",
      );
    } finally {
      setPlanning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pkg, key, choices]);

  // Dry run whenever the selection or a decision changes.
  useEffect(() => {
    if (open && result === null) void plan();
  }, [open, plan, result]);

  async function submit() {
    if (!resp) return;
    setSubmitting(true);
    setError(null);
    attempted.current = true;
    try {
      setResult(
        await api<DeletionResult>(token, "/publish/builds/delete", {
          method: "POST",
          body: JSON.stringify(
            deleteBody(pkg, versions, choices, reason, typed, resp.plan),
          ),
        }),
      );
    } catch (e) {
      if (
        e instanceof ApiError &&
        e.body &&
        typeof e.body === "object" &&
        "id" in e.body
      )
        setResult(e.body as DeletionResult);
      else
        setError(
          e instanceof ApiError ? e.message : "the deletion request failed",
        );
    } finally {
      setSubmitting(false);
    }
  }

  async function retry() {
    if (!result) return;
    setSubmitting(true);
    try {
      setResult(
        await api<DeletionResult>(
          token,
          `/publish/builds/deletions/${result.id}/resume`,
          {
            method: "POST",
          },
        ),
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "retry failed");
    } finally {
      setSubmitting(false);
    }
  }

  // A deletion still in flight is polled so the dialog shows it finish.
  useEffect(() => {
    if (!result || isFinal(result.state)) return;
    const t = setInterval(async () => {
      try {
        const r = await api<{ deletions: Array<DeletionResult> }>(
          token,
          `/publish/builds/deletions?package=${encodeURIComponent(pkg)}`,
        );
        const mine = r.deletions.find((d) => d.id === result.id);
        if (mine) setResult(mine);
      } catch {
        /* keep the last known state */
      }
    }, 5000);
    return () => clearInterval(t);
  }, [result, token, pkg]);

  function close() {
    if (attempted.current) onDone();
    onClose();
  }

  const phrase = confirmPhrase(pkg, versions);
  const refusals: PlanRefusal[] =
    resp && !resp.plan.ok ? resp.plan.refusals : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Delete{" "}
            {versions.length === 1 ? "build" : `${versions.length} builds`}{" "}
            <span className="font-mono">{pkg}</span>
          </DialogTitle>
          <DialogDescription>
            Removes the build for good from the registry (tarballs, platform
            packages, metadata), the publish pipeline&apos;s records and the
            stats history, and retires its version number. Withdraw keeps a
            build installable; this does not.
          </DialogDescription>
        </DialogHeader>

        <ul className="font-mono text-xs space-y-0.5">
          {versions.map((v) => (
            <li key={v}>{v}</li>
          ))}
        </ul>

        {result ? (
          <div className="space-y-2">
            <Alert
              variant={
                result.state === "done"
                  ? "success"
                  : result.state === "aborted"
                    ? "destructive"
                    : "warning"
              }
            >
              <AlertDescription>
                {deletionStatusLine(result.state, result.error)}
              </AlertDescription>
            </Alert>
            {!isFinal(result.state) && (
              <Button
                size="sm"
                variant="outline"
                disabled={submitting}
                onClick={retry}
              >
                Retry now
              </Button>
            )}
          </div>
        ) : (
          <>
            {planning && !resp && (
              <p className="text-sm text-muted-foreground">
                Listing what would be removed…
              </p>
            )}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {refusals.map((r, i) => (
              <Alert key={`${r.code}-${i}`} variant="warning">
                <AlertDescription className="space-y-2">
                  <p>{r.message}</p>
                  <div className="flex flex-wrap gap-2">
                    {actionsFor(r, resp!).map((a) => (
                      <Button
                        key={a.key}
                        size="sm"
                        variant="outline"
                        onClick={() => setChoices(a.apply(choices))}
                      >
                        {a.label}
                      </Button>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            ))}
            {resp && resp.plan.ok && (
              <div className="space-y-2">
                <p className="text-sm font-medium">What will be removed</p>
                <pre className="rounded-md border bg-muted/30 p-2 text-xs whitespace-pre-wrap break-words">
                  {describePlan(resp).join("\n")}
                </pre>
                <Alert variant="destructive">
                  <AlertDescription>{lockfileWarning(resp)}</AlertDescription>
                </Alert>
                <div className="space-y-1">
                  <label
                    className="text-sm font-medium"
                    htmlFor="delete-reason"
                  >
                    Reason (kept in the audit log)
                  </label>
                  <Input
                    id="delete-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="why these builds must be removed"
                  />
                </div>
                <div className="space-y-1">
                  <label
                    className="text-sm font-medium"
                    htmlFor="delete-confirm"
                  >
                    Type <span className="font-mono">{phrase}</span> to confirm
                  </label>
                  <Input
                    id="delete-confirm"
                    className="font-mono"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button
              variant="destructive"
              disabled={
                submitting || !canConfirm(pkg, versions, resp, reason, typed)
              }
              onClick={submit}
            >
              {submitting ? "Deleting…" : "Delete permanently"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
